import { Link, createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { api } from "../lib/api/api";
import { authClient } from "../lib/auth";
import type { ActiveUser, UsageMetrics } from "../types/usage";

export const Route = createFileRoute("/")({
  component: HomePage,
});

// Custom hooks for data fetching
function useUsageMetrics() {
  const [metrics, setMetrics] = useState<UsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMetrics() {
      try {
        const data = await api.getCurrentUsage();
        setMetrics(data);
        setNoDataAvailable(false);
        setError(null);
      } catch (error) {
        console.error("Failed to fetch usage metrics:", error);

        // Check if it's a "no data available" error (404)
        if (error instanceof Error && error.message.includes("404")) {
          setNoDataAvailable(true);
          setError(null);
        } else {
          setError(
            error instanceof Error
              ? error.message
              : "Failed to fetch usage metrics",
          );
          setNoDataAvailable(false);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchMetrics();
  }, []);

  return { metrics, loading, noDataAvailable, error };
}

function useActiveUsers() {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [noDataAvailable, setNoDataAvailable] = useState(false);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const data = await api.getActiveUsers();
        setUsers(data.users);
        setNoDataAvailable(false);
      } catch (error) {
        console.error("Failed to fetch active users:", error);

        // Check if it's a "no data available" error (404)
        if (error instanceof Error && error.message.includes("404")) {
          setNoDataAvailable(true);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  return { users, loading, noDataAvailable };
}

function HomePage() {
  // Get auth state from route context
  const { auth } = Route.useRouteContext();
  const {
    metrics,
    loading: metricsLoading,
    noDataAvailable,
    error,
  } = useUsageMetrics();
  const {
    users,
    loading: usersLoading,
    noDataAvailable: noUsersData,
  } = useActiveUsers();

  // Handle sign in with GitHub
  const handleSignIn = async () => {
    try {
      await authClient.signIn.social({
        provider: "github",
        callbackURL: "/",
      });
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  // If not authenticated, show login screen
  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Welcome to Honcalyzer
            </h2>
          </div>
          <div>
            <button
              type="button"
              onClick={handleSignIn}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <svg
                className="w-5 h-5 mr-3"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <title>GitHub</title>
                <path
                  fillRule="evenodd"
                  d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                  clipRule="evenodd"
                />
              </svg>
              Sign in with GitHub
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching metrics
  if (metricsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading usage metrics...</p>
        </div>
      </div>
    );
  }

  // Show error state if there's a real error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show "no data" state when data collection hasn't started yet
  if (noDataAvailable) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-blue-500 text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            No Usage Data Yet
          </h2>
          <p className="text-gray-600 mb-4">
            Usage analytics data will be available after the first scheduled
            collection runs. Data is collected every 10 minutes from the
            services-api database.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Check back in a few minutes, or explore the TypeScript compilation
            analysis instead.
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="block w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              Check Again
            </button>
            <Link
              to="/typescript-analysis"
              className="block w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              View TypeScript Analysis
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show dashboard for authenticated users with data
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-8">
        {/* Real Usage Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="New Users"
            current={`${metrics?.usersCreatedToday || 0} joined today`}
            total={`${metrics?.usersCreatedThisWeek || 0} this week`}
            icon="👥"
            color="bg-green-500"
          />
          <MetricCard
            title="Active Users"
            current={`${metrics?.activeUsersToday || 0} today`}
            total={`${metrics?.activeUsersThisWeek || 0} this week`}
            icon="🚀"
            color="bg-purple-500"
          />
          <MetricCard
            title="Total Chats"
            current={`${metrics?.chatsCreatedToday || 0} today`}
            total={`${metrics?.chatsCreatedThisWeek || 0} this week`}
            icon="💬"
            color="bg-orange-500"
          />
          <MetricCard
            title="Total Users"
            current={`${metrics?.totalUsers || 0} registered`}
            total={`${metrics?.totalChats || 0} total chats`}
            icon="📊"
            color="bg-blue-500"
          />
        </div>

        {/* Real Most Active Users */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Most Active Users This Week
            </h3>
          </div>
          <div className="border-t border-gray-200">
            {usersLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" />
                <p className="mt-2 text-sm text-gray-500">Loading users...</p>
              </div>
            ) : noUsersData ? (
              <div className="p-6 text-center">
                <p className="text-gray-500">
                  No active user data available yet
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Data will appear after the first collection cycle
                </p>
              </div>
            ) : users.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500">No active users this week</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {users.map((user, index) => (
                  <li key={user.name} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div
                            className={`w-8 h-8 rounded-full ${
                              index === 0
                                ? "bg-yellow-400"
                                : index === 1
                                  ? "bg-gray-400"
                                  : index === 2
                                    ? "bg-orange-400"
                                    : "bg-blue-400"
                            } flex items-center justify-center text-white font-bold text-sm`}
                          >
                            {index + 1}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || "Anonymous"}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.chats} chats
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Links to other sections */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Explore More
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/typescript-analysis"
              className="block p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              <h4 className="text-sm font-medium text-gray-900">
                TypeScript Analysis
              </h4>
              <p className="text-xs text-gray-500 mt-1">
                Browse compilation runs and inspect TypeScript errors
              </p>
            </Link>
            <div className="block p-4 border border-gray-200 rounded-lg opacity-50">
              <h4 className="text-sm font-medium text-gray-900">
                System Metrics
              </h4>
              <p className="text-xs text-gray-500 mt-1">
                Coming soon - Performance and system health metrics
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps {
  title: string;
  current: string;
  total: string;
  icon: string;
  color: string;
}

function MetricCard({ title, current, total, icon, color }: MetricCardProps) {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div
              className={`w-8 h-8 ${color} rounded-md flex items-center justify-center`}
            >
              <span className="text-white font-bold">{icon}</span>
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="text-lg font-medium text-gray-900">{current}</dd>
              <dd className="text-sm text-gray-600">{total}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QuickActionCardProps {
  title: string;
  description: string;
  link: string;
  buttonText: string;
  buttonColor: string;
  disabled?: boolean;
}

function QuickActionCard({
  title,
  description,
  link,
  buttonText,
  buttonColor,
  disabled = false,
}: QuickActionCardProps) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>
      {disabled ? (
        <button
          type="button"
          disabled
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${buttonColor}`}
        >
          {buttonText}
        </button>
      ) : (
        <Link
          to={link}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${buttonColor}`}
        >
          {buttonText}
        </Link>
      )}
    </div>
  );
}
